import type { Hit, Mode } from "@ranking-arena/shared/schemas";

/**
 * Pick the source URL for a hit, gated by mode. Returns `null` when no
 * mode-appropriate URL is present or when the candidate fails the scheme
 * allowlist.
 *
 * Mode routing:
 *   - slide-search → `hit.link_slide` (deep-link `<link_edit>#slide=N`;
 *     backend emits `null` when the slide number can't be derived).
 *   - doc-search   → `hit.new_url` first (canonical public URL), falling
 *     back to `hit.link_edit` (raw Google Slides edit URL).
 *
 * Scheme validation uses the `URL` constructor rather than a substring check.
 * A prefix check (`startsWith("http")`) can be bypassed by leading whitespace,
 * embedded `http` inside a `data:text/html,...` payload, or unusual encodings.
 * Only `http:` and `https:` are accepted — `javascript:`, `data:`, `blob:`,
 * `vbscript:`, `file:`, and malformed inputs all fall through to `null`.
 *
 * Any caller rendering the returned URL as `target="_blank"` MUST also set
 * `rel="noopener noreferrer"` to prevent the opened tab from reaching back
 * into the arena via `window.opener` and to strip the Referer header.
 */
export function resolveHitUrl(hit: Hit, mode: Mode): string | null {
  const candidate =
    mode === "slide-search"
      ? typeof hit.link_slide === "string" && hit.link_slide
        ? hit.link_slide
        : null
      : (typeof hit.new_url === "string" && hit.new_url) ||
        (typeof hit.link_edit === "string" && hit.link_edit) ||
        null;

  if (!candidate) return null;

  try {
    const parsed = new URL(candidate);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return null;
    return parsed.toString();
  } catch {
    return null;
  }
}
